import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LangContext } from '../../contexts/langContext';
import { SocialContext } from '../../contexts/socialContext';
import { StateContext } from '../../contexts/stateContext';
import { default_avatar } from '../../static/default_design';
import { MobMenu } from '../UiKit/MobMenu';
import { applyDesignTheme, applyYoniBlackBackground, AVATAR_TOKEN_LENGTH, HISTORY_INCLUDE_PENTAS, USER_TOKEN_LENGTH } from '../utilsDesign';
import { fetchUsers } from '../utilsSocial';
import { PreloaderHD } from '../UiKit/PreloaderHD';
import { PentasBrowser } from './PentasBrowser/PentasBrowser';
import {
  getPentaDesign,
  isOpenPentaMember,
  isSavedPentaToken,
  isUnsavedPentaRoute,
  replacePentaRouteSlot,
  UNSAVED_PENTA_OPEN_SLOT,
  unsavedPentaHasOpenSlot,
} from '../utilsPenta';




export const WithPenta = ({ children, bodyContainer, mode = 'hd' }) => {
  const context = { ...useContext(SocialContext), ...useContext(StateContext), ...useContext(LangContext) };
  const navigate = useNavigate();
  const location = useLocation();
  const [penta, setPenta] = useState();
  const [memberPickerOpen, setMemberPickerOpen] = useState(false);

  const { pentaId } = useParams();
  const isYoni = mode === 'yoni';

  useEffect(() => {
    if (typeof context.me === 'undefined') return;
    if (context.me && context.pentas == null) return;

    let cancelled = false;
    const callbackFail = () => {
      if (!cancelled) navigate('/404');
    };

    const fetchAvatarData = async () => {
      if (isUnsavedPentaRoute(pentaId)) {
        const slots = pentaId.split('-');
        const membersIds = slots.filter(one => one.length === USER_TOKEN_LENGTH);
        const membersIdsAvatars = slots.filter(one => one.length === AVATAR_TOKEN_LENGTH);
        const hasOpenSlot = slots.includes(UNSAVED_PENTA_OPEN_SLOT);

        const containsAllMembers = (onePentamembers, membersIds) => {
          // Extract tokens from onePentamembers
          const onePentamemberTokens = onePentamembers.map(member => member.user ? member.user.token : null);

          // Create sets from both arrays to compare
          const membersIdsSet = new Set(membersIds);
          const onePentamemberTokensSet = new Set(onePentamemberTokens);

          // Compare the sets for equality
          if (membersIdsSet.size !== onePentamemberTokensSet.size) {
            return false;
          }

          for (let token of onePentamemberTokensSet) {
            if (!membersIdsSet.has(token)) {
              return false;
            }
          }

          return true;
        };

        const existingPentas = context.pentas ? context.pentas.filter(onePenta => {
          return containsAllMembers(onePenta.members, membersIds);
        }) : [];




        if (!hasOpenSlot && existingPentas.length) {
          navigate(isYoni ? `/yoni/p/${existingPentas[0].token}` : `/p/${existingPentas[0].token}`);
        } else {
          // NO EXISTING PENTAS
          const pentaEmpty = {
            unsaved: true,
            my: true,
            count: 2,
            created: "2024-07-08T01:41:19+00:00",
            description: "description",
            members: [],
            name: null,
            permissions: 1,
            token: '',
            pentaDesign: ''
          };


          const avatarEntries = await Promise.all(membersIdsAvatars.map(async (oneAvatarId) => {
            const avatar = await context.x.getAvatar(oneAvatarId);
            if (!avatar?.token) return null;
            return [oneAvatarId, {
              user: null,
              avatar: {
                ...avatar,
                my: true
              }
            }];
          }));

          const emptyUser = {
            user: null,
            avatar: default_avatar
          }

          fetchUsers(membersIds, (members => {
            if (cancelled) return;
            const byToken = {};
            avatarEntries.filter(Boolean).forEach(([id, entry]) => {
              byToken[id] = entry;
            });
            Object.keys(members || {}).forEach((oneId) => {
              byToken[oneId] = members[oneId];
            });

            // Keep URL order, including an empty slot where the route says X.
            let nextMembers = slots.map((slot) => (
              slot === UNSAVED_PENTA_OPEN_SLOT ? emptyUser : byToken[slot]
            )).filter(Boolean);

            const realCount = nextMembers.filter((one) => !isOpenPentaMember(one)).length;
            if (!hasOpenSlot && realCount === 1 && nextMembers.length === 1) {
              nextMembers = [...nextMembers, emptyUser];
            }

            pentaEmpty.members = nextMembers;
            pentaEmpty.token = null;
            pentaEmpty.pentaDesign = getPentaDesign(pentaEmpty);
            setPenta(pentaEmpty);
          }));

        }

      }
    };

    fetchAvatarData();



    if (isSavedPentaToken(pentaId)) {
      context.x.getPenta(pentaId, (value) => {
        if (!cancelled) setPenta(value);
      }, callbackFail);
    }

    return () => {
      cancelled = true;
    };
  }, [pentaId, context.updatingPenta, context.me, context.pentas == null]);

  useEffect(() => {
    if (unsavedPentaHasOpenSlot(pentaId)) {
      setMemberPickerOpen(true);
    } else {
      setMemberPickerOpen(false);
    }
  }, [pentaId]);

  useEffect(() => {
    if (!isSavedPentaToken(pentaId) || !context.pentas?.length) return;
    const updated = context.pentas.find(one => one.token === pentaId);
    if (updated) {
      setPenta(updated);
    }
  }, [context.pentas, pentaId]);

  useEffect(() => {
    if (HISTORY_INCLUDE_PENTAS && penta?.token && isSavedPentaToken(pentaId)) {
      context.setState.setShowHistory(penta.token);
    }
  }, [penta?.token, pentaId]);

  useLayoutEffect(() => {
    if (!penta) return;
    if (isYoni) {
      // Keep the shared dark starfield look — don't paint HD design type colors.
      applyYoniBlackBackground();
      return;
    }
    const design = penta.pentaDesign || (penta.members?.length >= 2 ? getPentaDesign(penta) : null);
    applyDesignTheme(design);
  }, [penta?.token, penta?.pentaDesign?.type, penta?.members?.length, isYoni]);


  const pentaBase = isYoni ? '/yoni/p' : '/p';

  const pickMember = (userAvatar) => {
    const token = userAvatar?.user?.token || userAvatar?.avatar?.token;
    if (!token || !unsavedPentaHasOpenSlot(pentaId)) return;
    if (pentaId.split('-').includes(token)) return;
    const nextId = replacePentaRouteSlot(pentaId, UNSAVED_PENTA_OPEN_SLOT, token);
    setMemberPickerOpen(false);
    navigate(`${pentaBase}/${nextId}${location.search}${location.hash}`);
  };

  const parameters = {
    penta: penta,
    parentContainer: bodyContainer,
    mode,
    openMemberPicker: () => setMemberPickerOpen(true),
  };


  return <>

    {!penta && <PreloaderHD />}

    {Boolean(context.me) && <MobMenu
      penta={penta}
      mode={mode}
      showItems={isYoni
        ? ['people', 'history', 'bodygraphs', 'pentas']
        : undefined}
    />}

    {parameters.penta ? React.cloneElement(children, parameters) : null}

    {memberPickerOpen && context.me?.avatar && (
      <div className={`x_pentas_panel${!context.state.peoplePanel ? ' x_pentas_panel--only' : ''} x_pentas_panel--expanded`}>
        <div
          className="x_pentas_panel_z"
          onClick={() => setMemberPickerOpen(false)}
        />
        <PentasBrowser
          scope={['people', 'bodygraphs', 'celebs']}
          mode={mode}
          expanded
          customHandler={pickMember}
          handleClose={() => setMemberPickerOpen(false)}
        />
      </div>
    )}
  </>
};
